import { Box, Card, Container, SimpleGrid, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import {
  IconBrain,
  IconCube3dSphere,
  IconEngine,
  IconLibrary,
  IconSchool,
  IconShieldCheckFilled,
  type Icon,
} from '@tabler/icons-react';

import { Reveal } from '@/components/motion/Reveal';
import classes from './Features.module.css';

interface Feature {
  icon: Icon;
  title: string;
  body: string;
  accent: 'forge' | 'safety' | 'ai' | 'warn';
}

const FEATURES: Feature[] = [
  {
    icon: IconShieldCheckFilled,
    title: '3-tier safety engine',
    body:
      'Deterministic Rust rules, statistical guardrails, then the AI advisor — in that order. Danger blocks. Warning needs an ack. The engine never asks the LLM what to do.',
    accent: 'safety',
  },
  {
    icon: IconLibrary,
    title: 'Community tune library',
    body:
      'Browse, fork, and remix base maps from other tuners. Every shared tune carries the same deterministic safety verdict — so you always know what you’re flashing.',
    accent: 'forge',
  },
  {
    icon: IconEngine,
    title: 'Platform intelligence',
    body:
      '13 supported engine platforms — LS, K-series, EJ, 2JZ, B58, 4G63T, and more. Each one ships with a vetted rule pack, mod catalog, and base-map starter set.',
    accent: 'warn',
  },
  {
    icon: IconSchool,
    title: 'Built for beginners too',
    body:
      'Pick an experience level — Apprentice, Tuner, or Pro — and the editor scales the guard rails, jargon, and explanations to match. No more cliffs to fall off.',
    accent: 'safety',
  },
  {
    icon: IconBrain,
    title: 'AI-powered explanations',
    body:
      'Cell-level “Why?” answers and full-tune walk-throughs. Claude works as an advisor, not a decision maker — and falls back gracefully offline.',
    accent: 'ai',
  },
  {
    icon: IconCube3dSphere,
    title: '3D map visualization',
    body:
      'Toggle any table into a 3D surface. Spot lumps, dips, and unsafe gradients you would never see in a flat heatmap.',
    accent: 'forge',
  },
];

const ACCENT_MAP: Record<Feature['accent'], { bg: string; ring: string; color: string }> = {
  forge: {
    bg: 'rgba(255, 107, 44, 0.10)',
    ring: 'rgba(255, 107, 44, 0.35)',
    color: 'var(--tf-forge-start)',
  },
  safety: {
    bg: 'rgba(0, 230, 118, 0.08)',
    ring: 'rgba(0, 230, 118, 0.30)',
    color: 'var(--tf-safety)',
  },
  ai: {
    bg: 'rgba(187, 134, 252, 0.10)',
    ring: 'rgba(187, 134, 252, 0.30)',
    color: 'var(--tf-ai)',
  },
  warn: {
    bg: 'rgba(255, 179, 0, 0.10)',
    ring: 'rgba(255, 179, 0, 0.30)',
    color: 'var(--tf-warn)',
  },
};

export function Features() {
  return (
    <Box component="section" id="features" py={{ base: 64, md: 96 }} aria-labelledby="features-title">
      <Container size="xl">
        <Stack align="center" ta="center" gap="md" mb="xl">
          <Reveal>
            <Text size="sm" tt="uppercase" lts="0.12em" c="var(--tf-forge-start)" fw={700}>
              Built for tuners
            </Text>
          </Reveal>
          <Reveal delay={0.05}>
            <Title id="features-title" order={2} className={classes.heading}>
              Everything you need to tune well — and nothing that hides risk.
            </Title>
          </Reveal>
          <Reveal delay={0.1}>
            <Text c="var(--tf-text-secondary)" maw={680}>
              Four pillars, one cohesive workbench. Each feature is designed around the idea that a
              tuner should always know exactly why the tool is saying what it&rsquo;s saying.
            </Text>
          </Reveal>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {FEATURES.map((f, idx) => {
            const accent = ACCENT_MAP[f.accent];
            const Icon = f.icon;
            return (
              <Reveal key={f.title} delay={idx * 0.06}>
                <Card className={classes.card} h="100%" p="xl">
                  <Stack gap="md" h="100%">
                    <ThemeIcon
                      size={52}
                      radius="md"
                      variant="default"
                      styles={{
                        root: {
                          background: accent.bg,
                          borderColor: accent.ring,
                          color: accent.color,
                        },
                      }}
                    >
                      <Icon size={26} stroke={1.8} />
                    </ThemeIcon>
                    <Title order={3} fz="h4" c="var(--tf-text-primary)">
                      {f.title}
                    </Title>
                    <Text c="var(--tf-text-secondary)" size="sm" lh={1.6}>
                      {f.body}
                    </Text>
                  </Stack>
                </Card>
              </Reveal>
            );
          })}
        </SimpleGrid>
      </Container>
    </Box>
  );
}
